

const defaultSummary = {
  likes: 0,
  dislikes: 0,
  commentsCount: 0,
  userReaction: null
};

const FeedbackPanel = ({ targetType, itemId, initialSummary, compact = false, themeMode = 'night' }) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [summary, setSummary] = useState(initialSummary || defaultSummary);
  const [comments, setComments] = useState([]);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [reactionLoading, setReactionLoading] = useState('');
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState('');
  const [deletingReplyId, setDeletingReplyId] = useState('');
  const [reportingCommentId, setReportingCommentId] = useState('');
  const [reportingReplyId, setReportingReplyId] = useState('');
  const [commentReactionLoading, setCommentReactionLoading] = useState('');
  const [replySubmittingCommentId, setReplySubmittingCommentId] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({});
  const [reportModal, setReportModal] = useState({ open: false, targetType: 'comment', targetId: '' });
  const [reportForm, handleReportChange, setReportForm] = useForm({ reason: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setSummary({ ...defaultSummary, ...(initialSummary || {}) });
  }, [initialSummary]);

  useEffect(() => {
    const socket = getSharedRealtimeSocket();
    const handleFeedbackUpdate = (payload) => {
      if (String(payload?.targetType || '') !== String(targetType) || String(payload?.targetId || '') !== String(itemId)) {
        return;
      }

      setSummary((prev) => ({
        ...prev,
        likes: Number(payload?.summary?.likes || 0),
        dislikes: Number(payload?.summary?.dislikes || 0),
        commentsCount: Number(payload?.summary?.commentsCount || 0)
      }));

      if (commentsOpen) {
        loadComments();
      }
    };

    socket.emit('feedback:subscribe', { targetType, targetId: itemId });
    socket.on('feedback:update', handleFeedbackUpdate);

    return () => {
      socket.emit('feedback:unsubscribe', { targetType, targetId: itemId });
      socket.off('feedback:update', handleFeedbackUpdate);
    };
  }, [commentsOpen, itemId, targetType]);

  const loadComments = async () => {
    setCommentsLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await api.get(`/feedback/${targetType}/${itemId}/comments`);
      setComments(response.data?.item?.comments || []);
      setSummary((prev) => ({
        ...prev,
        likes: response.data?.item?.likes ?? prev.likes,
        dislikes: response.data?.item?.dislikes ?? prev.dislikes,
        commentsCount: response.data?.item?.comments?.length ?? prev.commentsCount,
        userReaction: response.data?.item?.userReaction ?? prev.userReaction
      }));
    } catch (requestError) {
      const msg = requestError.response?.data?.message || 'Сэтгэгдэл унших үед алдаа гарлаа';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setCommentsLoading(false);
    }
  };

  const toggleComments = async () => {
    const nextOpen = !commentsOpen;
    setCommentsOpen(nextOpen);

    if (nextOpen) {
      await loadComments();
    }
  };

  const handleReaction = async (reaction) => {
    if (!user) {
      const msg = 'Like, dislike, comment хийхийн тулд нэвтэрнэ үү';
      setError(msg);
      setMessage('');
      showToast(msg, 'error');
      return;
    }
    setReactionLoading(reaction);
    setError('');
    setMessage('');
    try {
      const nextReaction = summary.userReaction === reaction ? 'none' : reaction;
      const response = await api.post(`/feedback/${targetType}/${itemId}/reaction`, {
        reaction: nextReaction
      });
      setSummary((prev) => ({ ...prev, ...(response.data?.item || {}), commentsCount: prev.commentsCount }));
      showToast('Reaction хадгалагдлаа', 'success');
    } catch (requestError) {
      const msg = requestError.response?.data?.message || 'Reaction хадгалах үед алдаа гарлаа';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setReactionLoading('');
    }
  };

  const handleCommentSubmit = async (event) => {
    event.preventDefault();
    if (!user) {
      const msg = 'Сэтгэгдэл бичихийн тулд нэвтэрнэ үү';
      setError(msg);
      setMessage('');
      showToast(msg, 'error');
      return;
    }
    const text = commentText.trim();
    if (!text) {
      const msg = 'Сэтгэгдэл хоосон байж болохгүй';
      setError(msg);
      setMessage('');
      showToast(msg, 'error');
      return;
    }
    setCommentSubmitting(true);
    setError('');
    setMessage('');
    try {
      const response = await api.post(`/feedback/${targetType}/${itemId}/comments`, { text });
      const createdComment = response.data?.item?.comment;
      const nextSummary = response.data?.item?.summary || summary;
      if (createdComment) {
        setComments((prev) => [createdComment, ...prev]);
      }
      setSummary((prev) => ({ ...prev, ...nextSummary }));
      setCommentText('');
      setCommentsOpen(true);
      showToast('Сэтгэгдэл илгээгдлээ', 'success');
    } catch (requestError) {
      const msg = requestError.response?.data?.message || 'Сэтгэгдэл хадгалах үед алдаа гарлаа';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    setDeletingCommentId(commentId);
    setError('');
    setMessage('');
    try {
      const response = await api.delete(`/feedback/comments/${commentId}`);
      setComments((prev) => prev.filter((comment) => comment._id !== commentId));
      setSummary((prev) => ({ ...prev, ...(response.data?.item || {}) }));
      showToast('Сэтгэгдэл устгагдлаа', 'success');
    } catch (requestError) {
      const msg = requestError.response?.data?.message || 'Сэтгэгдэл устгах үед алдаа гарлаа';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setDeletingCommentId('');
    }
  };

  const openReportModal = (commentId) => {
    if (!user) {
      const msg = 'Сэтгэгдэл report хийхийн тулд нэвтэрнэ үү';
      setError(msg);
      setMessage('');
      showToast(msg, 'error');
      return;
    }
    setReportModal({ open: true, targetType: 'comment', targetId: commentId });
    setReportForm({ reason: '' });
  };

  const openReplyReportModal = (replyId) => {
    if (!user) {
      const msg = 'Reply report хийхийн тулд нэвтэрнэ үү';
      setError(msg);
      setMessage('');
      showToast(msg, 'error');
      return;
    }
    setReportModal({ open: true, targetType: 'reply', targetId: replyId });
    setReportForm({ reason: '' });
  };

  const closeReportModal = () => {
    setReportModal({ open: false, targetType: 'comment', targetId: '' });
    setReportForm({ reason: '' });
  };

  const handleReportComment = async () => {
    const targetId = reportModal.targetId;
    if (!targetId) {
      return;
    }
    if (reportModal.targetType === 'reply') {
      setReportingReplyId(targetId);
    } else {
      setReportingCommentId(targetId);
    }
    setError('');
    setMessage('');
    try {
      const endpoint = reportModal.targetType === 'reply'
        ? `/feedback/replies/${targetId}/report`
        : `/feedback/comments/${targetId}/report`;
      const response = await api.post(endpoint, { reason: reportForm.reason.trim() });
      if (reportModal.targetType === 'reply') {
        setComments((prev) => prev.map((comment) => ({
          ...comment,
          replies: (comment.replies || []).map((reply) => (
            String(reply._id) === String(targetId)
              ? { ...reply, canReport: false, reportCount: Number(reply.reportCount || 0) + 1 }
              : reply
          ))
        })));
      } else {
        setComments((prev) => prev.map((comment) => (
          comment._id === targetId
            ? { ...comment, canReport: false, reportCount: Number(comment.reportCount || 0) + 1 }
            : comment
        )));
      }
      const msg = response.data?.message || 'Report амжилттай илгээгдлээ';
      setMessage(msg);
      showToast(msg, 'success');
      closeReportModal();
    } catch (requestError) {
      const msg = requestError.response?.data?.message || 'Report илгээх үед алдаа гарлаа';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setReportingCommentId('');
      setReportingReplyId('');
    }
  };

  const handleDeleteReply = async (replyId) => {
    setDeletingReplyId(replyId);
    setError('');
    setMessage('');
    try {
      const response = await api.delete(`/feedback/replies/${replyId}`);
      const removedReplyId = String(response.data?.item?.replyId || replyId);
      setComments((prev) => prev.map((comment) => ({
        ...comment,
        replies: (comment.replies || []).filter((reply) => String(reply._id) !== removedReplyId)
      })));
      showToast('Reply устгагдлаа', 'success');
    } catch (requestError) {
      const msg = requestError.response?.data?.message || 'Reply устгах үед алдаа гарлаа';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setDeletingReplyId('');
    }
  };

  const handleCommentReaction = async (comment, reaction) => {
    if (!user) {
      setError('Reaction хийхийн тулд нэвтэрнэ үү');
      setMessage('');
      return;
    }

    const commentId = String(comment._id);
    const nextReaction = comment.userReaction === reaction ? 'none' : reaction;

    setCommentReactionLoading(`${commentId}:${reaction}`);
    setError('');
    setMessage('');

    try {
      const response = await api.post(`/feedback/comments/${commentId}/reaction`, { reaction: nextReaction });
      const nextItem = response.data?.item || {};

      setComments((prev) => prev.map((item) => (
        String(item._id) === commentId
          ? {
            ...item,
            likes: Number(nextItem.likes || 0),
            dislikes: Number(nextItem.dislikes || 0),
            userReaction: nextItem.userReaction || null
          }
          : item
      )));
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Comment reaction хадгалах үед алдаа гарлаа');
    } finally {
      setCommentReactionLoading('');
    }
  };

  const handleReplySubmit = async (commentId) => {
    if (!user) {
      const msg = 'Reply бичихийн тулд нэвтэрнэ үү';
      setError(msg);
      setMessage('');
      showToast(msg, 'error');
      return;
    }
    const text = String(replyDrafts[commentId] || '').trim();
    if (!text) {
      const msg = 'Reply хоосон байж болохгүй';
      setError(msg);
      setMessage('');
      showToast(msg, 'error');
      return;
    }
    setReplySubmittingCommentId(commentId);
    setError('');
    setMessage('');
    try {
      const response = await api.post(`/feedback/comments/${commentId}/replies`, { text });
      const reply = response.data?.item?.reply;
      if (reply) {
        setComments((prev) => prev.map((item) => (
          String(item._id) === String(commentId)
            ? {
              ...item,
              replies: [reply, ...(item.replies || [])]
            }
            : item
        )));
      }
      setReplyDrafts((prev) => ({ ...prev, [commentId]: '' }));
      showToast('Reply илгээгдлээ', 'success');
    } catch (requestError) {
      const msg = requestError.response?.data?.message || 'Reply нэмэх үед алдаа гарлаа';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setReplySubmittingCommentId('');
    }
  };

  return (
    <section className={`feedback-panel feedback-theme-${themeMode} ${compact ? 'feedback-compact' : ''}`}>
      <div className="feedback-actions-row">
        <button
          type="button"
          className={`feedback-action-btn ${summary.userReaction === 'like' ? 'active' : ''}`}
          disabled={reactionLoading === 'like' || reactionLoading === 'dislike'}
          onClick={() => handleReaction('like')}
        >
          {reactionLoading === 'like' ? '...' : 'Like'} {summary.likes}
        </button>
        <button
          type="button"
          className={`feedback-action-btn ${summary.userReaction === 'dislike' ? 'active dislike' : 'dislike'}`}
          disabled={reactionLoading === 'like' || reactionLoading === 'dislike'}
          onClick={() => handleReaction('dislike')}
        >
          {reactionLoading === 'dislike' ? '...' : 'Dislike'} {summary.dislikes}
        </button>
        <button type="button" className="feedback-comments-toggle" onClick={toggleComments}>
          Comment {summary.commentsCount}
        </button>
      </div>

      {commentsOpen && (
        <div className="feedback-comments-box">
          <form className="feedback-comment-form" onSubmit={handleCommentSubmit}>
            <textarea
              value={commentText}
              onChange={(event) => setCommentText(event.target.value)}
              placeholder={user ? 'Сэтгэгдлээ бичнэ үү' : 'Сэтгэгдэл бичихийн тулд нэвтэрнэ үү'}
              disabled={!user || commentSubmitting}
              rows={compact ? 2 : 3}
            />
            <button type="submit" disabled={!user || commentSubmitting}>
              {commentSubmitting ? 'Илгээж байна...' : 'Илгээх'}
            </button>
          </form>

          {/* Toast мэдэгдэл рүү шилжүүлсэн */}
          {commentsLoading && <div className="feedback-loading">Сэтгэгдэл уншиж байна...</div>}
          {!commentsLoading && comments.length === 0 && <div className="feedback-empty">Одоогоор сэтгэгдэл алга</div>}
          {!commentsLoading && comments.length > 0 && (
            <div className="feedback-comments-list">
              {comments.map((comment) => (
                <article key={comment._id} className="feedback-comment-item">
                  <div className="feedback-comment-head">
                    <strong>{comment.username}</strong>
                    <time>{new Date(comment.createdAt).toLocaleString()}</time>
                  </div>
                  <p>{comment.text}</p>
                  <div className="feedback-actions-row" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className={`feedback-action-btn ${comment.userReaction === 'like' ? 'active' : ''}`}
                      disabled={commentReactionLoading === `${comment._id}:like` || commentReactionLoading === `${comment._id}:dislike`}
                      onClick={() => handleCommentReaction(comment, 'like')}
                    >
                      {commentReactionLoading === `${comment._id}:like` ? '...' : 'Like'} {Number(comment.likes || 0)}
                    </button>
                    <button
                      type="button"
                      className={`feedback-action-btn ${comment.userReaction === 'dislike' ? 'active dislike' : 'dislike'}`}
                      disabled={commentReactionLoading === `${comment._id}:like` || commentReactionLoading === `${comment._id}:dislike`}
                      onClick={() => handleCommentReaction(comment, 'dislike')}
                    >
                      {commentReactionLoading === `${comment._id}:dislike` ? '...' : 'Dislike'} {Number(comment.dislikes || 0)}
                    </button>
                  </div>

                  <div className="feedback-comment-form" style={{ marginTop: 10 }}>
                    <textarea
                      value={replyDrafts[comment._id] || ''}
                      onChange={(event) => setReplyDrafts((prev) => ({ ...prev, [comment._id]: event.target.value }))}
                      placeholder={user ? 'Comment дээр хариу бичих' : 'Reply бичихийн тулд нэвтэрнэ үү'}
                      disabled={!user || replySubmittingCommentId === comment._id}
                      rows={2}
                    />
                    <button
                      type="button"
                      disabled={!user || replySubmittingCommentId === comment._id}
                      onClick={() => handleReplySubmit(comment._id)}
                    >
                      {replySubmittingCommentId === comment._id ? 'Илгээж байна...' : 'Reply'}
                    </button>
                  </div>

                  {Array.isArray(comment.replies) && comment.replies.length > 0 && (
                    <div className="feedback-comments-list" style={{ marginTop: 10 }}>
                      {comment.replies.map((reply) => (
                        <article key={reply._id} className="feedback-comment-item" style={{ marginLeft: 20 }}>
                          <div className="feedback-comment-head">
                            <strong>{reply.username}</strong>
                            <time>{new Date(reply.createdAt).toLocaleString()}</time>
                          </div>
                          <p>{reply.text}</p>
                          {reply.isHidden && (
                            <div className="feedback-hidden-note">Энэ reply нуугдсан байна {reply.hiddenReason ? `- ${reply.hiddenReason}` : ''}</div>
                          )}
                          {reply.canDelete && (
                            <button
                              type="button"
                              className="feedback-comment-delete"
                              disabled={deletingReplyId === reply._id}
                              onClick={() => handleDeleteReply(reply._id)}
                            >
                              {deletingReplyId === reply._id ? 'Устгаж байна...' : 'Reply устгах'}
                            </button>
                          )}
                          {reply.canReport && (
                            <button
                              type="button"
                              className="feedback-comment-delete"
                              disabled={reportingReplyId === reply._id}
                              onClick={() => openReplyReportModal(reply._id)}
                            >
                              {reportingReplyId === reply._id ? 'Илгээж байна...' : 'Reply report'}
                            </button>
                          )}
                        </article>
                      ))}
                    </div>
                  )}
                  {comment.isHidden && (
                    <div className="feedback-hidden-note">Энэ сэтгэгдэл нуугдсан байна {comment.hiddenReason ? `- ${comment.hiddenReason}` : ''}</div>
                  )}
                  {comment.canDelete && (
                    <button
                      type="button"
                      className="feedback-comment-delete"
                      disabled={deletingCommentId === comment._id}
                      onClick={() => handleDeleteComment(comment._id)}
                    >
                      {deletingCommentId === comment._id ? 'Устгаж байна...' : 'Устгах'}
                    </button>
                  )}
                  {comment.canReport && (
                    <button
                      type="button"
                      className="feedback-comment-delete"
                      disabled={reportingCommentId === comment._id}
                      onClick={() => openReportModal(comment._id)}
                    >
                      {reportingCommentId === comment._id ? 'Илгээж байна...' : 'Report'}
                    </button>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Toast мэдэгдэл рүү шилжүүлсэн */}

      {/* Report reason modal */}
      {reportModal.open && (
        <div className="modal-backdrop" onClick={closeReportModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ minWidth: 340 }}>
            <h4>Report шалтгаан</h4>
            <form onSubmit={e => { e.preventDefault(); handleReportComment(); }}>
              <FormField
                type="textarea"
                label="Шалтгаан"
                name="reason"
                value={reportForm.reason}
                onChange={handleReportChange}
                required
                placeholder="Яагаад report хийж байгаагаа бичнэ үү"
                rows={3}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button type="submit" className="btn-primary">Report</button>
                <button type="button" className="btn-secondary" onClick={closeReportModal}>Болих</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {reportModal.open && (
        <div className="feedback-dialog-backdrop" onClick={closeReportModal}>
          <div className="feedback-dialog" onClick={(event) => event.stopPropagation()}>
            <h4>Report шалтгаан</h4>
            <textarea
              value={reportModal.reason}
              onChange={(event) => setReportModal((prev) => ({ ...prev, reason: event.target.value }))}
              placeholder="Энэ comment яагаад асуудалтай гэж үзэж байгаагаа бичиж болно"
              rows={4}
            />
            <div className="feedback-dialog-actions">
              <button type="button" className="feedback-comment-delete" onClick={closeReportModal}>Болих</button>
              <button
                type="button"
                className="feedback-comments-toggle"
                disabled={
                  (reportModal.targetType === 'comment' && reportingCommentId === reportModal.targetId)
                  || (reportModal.targetType === 'reply' && reportingReplyId === reportModal.targetId)
                }
                onClick={handleReportComment}
              >
                {(
                  (reportModal.targetType === 'comment' && reportingCommentId === reportModal.targetId)
                  || (reportModal.targetType === 'reply' && reportingReplyId === reportModal.targetId)
                ) ? 'Илгээж байна...' : 'Report илгээх'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default FeedbackPanel;